import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  History,
  Search,
  ShieldAlert,
  ShieldCheck,
  UserRound,
  X,
} from "lucide-react";
import { useAdminCompliance, useAmlRiskDecisions } from "../../hooks/useAdmin";
import { usePermissions, PERMISSIONS } from "../../hooks/usePermissions";
import { useFilterParams } from "../../hooks/useFilterParams";
import { ConfirmActionModal } from "./ConfirmActionModal";
import { Pagination } from "./Pagination";
import { AdminSanctionsReview } from "./AdminSanctionsReview";
import { readApiError } from "../../utils/readApiError";

const PAGE_SIZE = 25;

const FILTER_DEFAULTS = {
  search: "",
  risk_level: "all",
  offset: 0,
};

const DECISIONS = [
  { value: "clear", label: "Clear", destructive: false },
  { value: "review_required", label: "Review required", destructive: true },
  { value: "blocked", label: "Blocked", destructive: true },
];

export function AdminCompliance() {
  const { values, setValues, setFilters, clearFilters, isFiltered } =
    useFilterParams(FILTER_DEFAULTS);
  const { search, risk_level: selectedRisk, offset } = values;

  const [searchInput, setSearchInput] = useState(search);
  const [actionError, setActionError] = useState(null);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [evidenceCase, setEvidenceCase] = useState(null);

  const { can } = usePermissions();
  const canDecide = can(PERMISSIONS.COMPLIANCE_DECIDE);
  const canReadAml = can(PERMISSIONS.AML_READ);
  const canReadUsers = can(PERMISSIONS.USERS_READ);
  const canReadAudit = can(PERMISSIONS.AUDIT_READ);

  useEffect(() => {
    setSearchInput((current) => (current === search ? current : search));
  }, [search]);

  useEffect(() => {
    const typed = searchInput.trim();
    if (typed === search) return undefined;
    const timer = setTimeout(
      () => setValues({ search: typed, offset: 0 }, { replace: true }),
      300
    );
    return () => clearTimeout(timer);
  }, [searchInput, search, setValues]);

  const params = useMemo(
    () => ({ limit: PAGE_SIZE, offset, search, riskLevel: selectedRisk }),
    [offset, search, selectedRisk]
  );

  // Search, the risk filter and the counters are all server-side. Filtering a
  // capped page in the browser missed every case past the first page, and
  // counting `cases.length` reported the page size as the queue size.
  const {
    cases,
    total,
    totalOpen,
    totalHighRisk,
    totalBlocked,
    isLoading,
    error,
    updateComplianceStatus,
    isUpdatingCompliance,
  } = useAdminCompliance(params);

  async function confirmDecision(reason) {
    if (!pendingDecision) return;
    setActionError(null);
    try {
      await updateComplianceStatus({
        userId: pendingDecision.userId,
        status: pendingDecision.status,
        reason,
      });
      setPendingDecision(null);
    } catch (err) {
      setActionError(readApiError(err, "Could not save the compliance decision."));
    }
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 mb-1">
          Security &amp; AML
        </h2>
        <h1 className="text-2xl md:text-3xl text-[#0A4730] font-serif font-semibold">
          Compliance &amp; Risk
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <SummaryCard
          title="Open review cases"
          value={totalOpen}
          caption="KYC or compliance attention needed"
          tone="amber"
        />
        <SummaryCard
          title="High-risk cases"
          value={totalHighRisk}
          caption="Rejected, blocked, or manual review"
          tone="red"
        />
        <SummaryCard
          title="Blocked accounts"
          value={totalBlocked}
          caption="Accounts prevented from transacting"
          tone="green"
        />
      </div>

      <AdminSanctionsReview />

      {/* While a decision dialog is open the error belongs in the dialog, so
          it is not repeated here. */}
      {(error || (actionError && !pendingDecision)) && (
        <div className="rounded-2xl bg-red-50 border border-red-100 p-4 text-sm text-red-600">
          {actionError || "Could not load the compliance queue."}
        </div>
      )}

      <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-[0_4px_20px_-5px_rgba(0,0,0,0.05)] space-y-6">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400"
              size={18}
            />
            <input
              aria-label="Search compliance cases"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by name, email, or user ID…"
              className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm focus:outline-none focus:border-[#0A4730] focus:ring-4 focus:ring-[#0A4730]/5"
            />
          </div>
          <div className="flex items-center gap-1 bg-[#FDFDFD] p-1 rounded-2xl border border-slate-200 w-full md:w-auto">
            {["all", "high", "medium"].map((risk) => (
              <button
                key={risk}
                onClick={() => setFilters({ risk_level: risk })}
                className={`px-4 py-2 rounded-xl text-xs font-semibold capitalize ${
                  selectedRisk === risk ? "bg-[#0A4730] text-white" : "text-[#0A2540]/70"
                }`}
              >
                {risk}
                {risk !== "all" ? " risk" : ""}
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-slate-100">
                <Header>User</Header>
                <Header>Review reason</Header>
                <Header>Risk</Header>
                <Header>KYC</Header>
                <Header>Compliance</Header>
                <Header align="right">Decision</Header>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {isLoading ? (
                <EmptyRow text="Loading compliance cases…" />
              ) : cases.length === 0 ? (
                <EmptyRow
                  text={
                    isFiltered
                      ? "No compliance cases match these filters."
                      : "No open compliance cases. Nothing needs review."
                  }
                  action={
                    isFiltered ? (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-2 text-xs font-semibold text-[#0A4730] underline underline-offset-4"
                      >
                        Clear filters
                      </button>
                    ) : null
                  }
                />
              ) : (
                cases.map((item) => (
                  <tr key={item.id} className="hover:bg-[#F5F9F4]/50 transition-colors">
                    <td className="py-4 pr-4">
                      <div className="font-medium text-[#0A2540]">
                        {item.user_name || "Unnamed user"}
                      </div>
                      <div className="text-xs text-slate-400">
                        {item.user_email || item.user_id}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5">
                        {canReadUsers && (
                          <Link
                            to={`/admin/users?search=${encodeURIComponent(item.user_id)}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A4730] hover:underline underline-offset-4"
                          >
                            <UserRound size={12} /> Account
                          </Link>
                        )}
                        {canReadAudit && (
                          <Link
                            to={`/admin/audit?target_type=user&target_id=${item.user_id}`}
                            className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A4730] hover:underline underline-offset-4"
                          >
                            <History size={12} /> History
                          </Link>
                        )}
                      </div>
                    </td>
                    <td className="py-4 pr-4 text-sm text-[#0A2540]/80">{item.trigger}</td>
                    <td className="py-4 pr-4">
                      <RiskBadge level={item.risk_level} />
                    </td>
                    <td className="py-4 pr-4 text-xs font-semibold text-slate-500 capitalize">
                      {humanize(item.kyc_status)}
                    </td>
                    <td className="py-4 pr-4 text-xs font-semibold text-slate-500 capitalize">
                      {humanize(item.compliance_status)}
                    </td>
                    <td className="py-4 text-right">
                      <div className="inline-flex items-center gap-2">
                        {canReadAml && (
                          <button
                            onClick={() => setEvidenceCase(item)}
                            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl border border-slate-200 bg-white text-[#0A4730] font-semibold text-xs hover:bg-slate-50"
                          >
                            Evidence <ChevronRight size={13} />
                          </button>
                        )}
                        {canDecide ? (
                          <div className="inline-flex gap-1">
                            {DECISIONS.filter(
                              (decision) => decision.value !== item.compliance_status
                            ).map((decision) => (
                              <button
                                key={decision.value}
                                onClick={() =>
                                  setPendingDecision({
                                    userId: item.user_id,
                                    status: decision.value,
                                    label: decision.label,
                                    destructive: decision.destructive,
                                    userName: item.user_name || item.user_email,
                                  })
                                }
                                className={`px-3 py-2 rounded-xl text-xs font-semibold border transition ${
                                  decision.destructive
                                    ? "border-red-200 text-red-600 hover:bg-red-50"
                                    : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                                }`}
                              >
                                {decision.label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Read-only</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          total={total}
          limit={PAGE_SIZE}
          offset={offset}
          onChange={(next) => setValues({ offset: next })}
          isLoading={isLoading}
        />
      </div>

      {evidenceCase && (
        <EvidenceDrawer
          caseItem={evidenceCase}
          onClose={() => setEvidenceCase(null)}
        />
      )}

      {/* Decisions are never applied on a single click: blocking a customer
          stops them moving money, so it takes an explicit confirmation and a
          reason that lands in the audit log. */}
      <ConfirmActionModal
        open={Boolean(pendingDecision)}
        title={
          pendingDecision
            ? `Set ${pendingDecision.userName || "this customer"} to “${pendingDecision.label}”?`
            : ""
        }
        description={
          pendingDecision?.status === "blocked"
            ? "A blocked customer cannot send money. They are not told the internal reason."
            : pendingDecision?.status === "review_required"
            ? "The customer is flagged for manual review before further transfers."
            : "This clears the customer to transact normally."
        }
        confirmLabel={pendingDecision?.label}
        destructive={pendingDecision?.destructive}
        requireReason={pendingDecision?.destructive}
        error={actionError}
        isSubmitting={isUpdatingCompliance}
        onConfirm={confirmDecision}
        onCancel={() => {
          setPendingDecision(null);
          setActionError(null);
        }}
      />
    </div>
  );
}

/**
 * The AML decisions behind a case.
 *
 * Previously a reviewer had to decide with nothing in front of them; the
 * backend already recorded these decisions but nothing surfaced them.
 */
function EvidenceDrawer({ caseItem, onClose }) {
  const { data: decisions = [], isLoading, error } = useAmlRiskDecisions(
    caseItem.user_id
  );
  const closeButtonRef = useRef(null);

  // Same treatment as ConfirmActionModal: take focus so a keyboard user is not
  // tabbing through the page behind the overlay, and close on Escape.
  useEffect(() => {
    closeButtonRef.current?.focus();
    function onKeyDown(event) {
      if (event.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
      <div
        role="dialog"
        aria-modal="true"
        aria-label="AML evidence"
        className="bg-white rounded-3xl max-w-2xl w-full p-6 space-y-5 shadow-2xl border border-slate-100 max-h-[85vh] overflow-y-auto"
      >
        <div className="flex items-start justify-between gap-4 border-b border-slate-100 pb-4">
          <div>
            <p className="text-[9px] uppercase tracking-widest text-slate-400">
              AML evidence
            </p>
            <h3 className="font-semibold text-[#0A2540]">
              {caseItem.user_name || caseItem.user_email || caseItem.user_id}
            </h3>
            <p className="text-xs text-slate-400 font-mono mt-0.5">{caseItem.user_id}</p>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close evidence"
            className="p-2 rounded-xl text-slate-400 hover:bg-slate-50 transition"
          >
            <X size={18} />
          </button>
        </div>

        {isLoading ? (
          <p className="text-sm text-slate-400 py-6">Loading AML decisions…</p>
        ) : error ? (
          <p className="text-sm text-red-500 py-6">
            Could not load AML decisions. This requires the <code>aml:read</code>{" "}
            permission.
          </p>
        ) : decisions.length === 0 ? (
          <p className="text-sm text-slate-400 py-6">
            No AML risk decisions recorded for this customer.
          </p>
        ) : (
          <div className="space-y-3">
            {decisions.map((decision) => (
              <div
                key={decision.id}
                className="rounded-2xl border border-slate-100 bg-[#FDFDFD] p-4 space-y-2"
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm font-semibold text-[#0A2540] capitalize">
                    {humanize(decision.decision)}
                  </span>
                  <span className="text-xs text-slate-400">
                    {formatDate(decision.created_at)}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-xs">
                  <Field label="Reason code" value={decision.reason_code || "—"} />
                  <Field
                    label="Amount"
                    value={`${decision.send_amount} ${decision.send_currency} → ${decision.receive_currency}`}
                  />
                  <Field label="Funding" value={decision.funding_method || "—"} />
                  <Field label="Transfer" value={decision.transfer_id || "—"} />
                </div>
                {decision.internal_detail && (
                  <p className="text-xs text-[#0A2540]/70 bg-white border border-slate-100 rounded-xl p-3">
                    {decision.internal_detail}
                  </p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-slate-400 mb-0.5">{label}</p>
      <p className="text-[#0A2540] break-all">{value}</p>
    </div>
  );
}

function SummaryCard({ title, value, caption, tone }) {
  const color =
    tone === "red" ? "text-red-500" : tone === "amber" ? "text-amber-600" : "text-[#0A4730]";
  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm space-y-2">
      <span className="text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40">
        {title}
      </span>
      <div className={`text-2xl font-serif font-semibold ${color}`}>{value}</div>
      <p className="text-xs text-slate-400">{caption}</p>
    </div>
  );
}

function Header({ children, align = "left" }) {
  return (
    <th
      className={`pb-4 text-[9px] font-semibold uppercase tracking-[0.2em] text-[#0A2540]/40 ${
        align === "right" ? "text-right" : ""
      }`}
    >
      {children}
    </th>
  );
}

function EmptyRow({ text, action = null }) {
  return (
    <tr>
      <td colSpan="6" className="py-10 text-center text-sm text-[#0A2540]/50">
        {text}
        {action}
      </td>
    </tr>
  );
}

function RiskBadge({ level }) {
  const config =
    level === "high"
      ? { style: "bg-red-50 text-red-500 border-red-100", icon: ShieldAlert }
      : level === "medium"
      ? { style: "bg-amber-50 text-amber-600 border-amber-100", icon: AlertTriangle }
      : { style: "bg-slate-50 text-slate-600 border-slate-100", icon: ShieldCheck };
  const Icon = config.icon;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border capitalize ${config.style}`}
    >
      <Icon size={14} />
      {level}
    </span>
  );
}


function humanize(value = "") {
  return String(value).replaceAll("_", " ");
}

function formatDate(value) {
  return value
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(value))
    : "—";
}
